import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import type {
  Location,
  LocationsResponse,
  LocationFilters,
  CreateLocationDto,
  UpdateLocationDto,
  LocationStatistics,
} from '../../types/location.types';

// Получение списка локаций
export const useLocations = (filters: LocationFilters = {}) => {
  return useQuery({
    queryKey: ['locations', filters],
    queryFn: async () => {
      try {
        const response = await apiClient.getLocations(filters);

        // Преобразуем ответ в ожидаемый формат
        const result = {
          data: response.data || [],
          total: response.total || 0,
          page: response.page || 1,
          limit: response.limit || 10,
        } as LocationsResponse;
        return result;
      } catch (error) {
        console.error('[useLocations] Error fetching locations:', error);
        throw error;
      }
    },
  });
};

// Получение одной локации
export const useLocation = (id: string | undefined) => {
  return useQuery({
    queryKey: ['locations', id],
    queryFn: async () => {
      if (!id) throw new Error('Location ID is required');
      const response = await apiClient.getLocation(id);
      return response.data as Location;
    },
    enabled: !!id,
  });
};

// Создание локации
export const useCreateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateLocationDto) => {
      const response = await apiClient.createLocation(data);
      return response.data as Location;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
};

// Обновление локации
export const useUpdateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateLocationDto }) => {
      const response = await apiClient.updateLocation(id, data);
      return response.data as Location;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['locations', variables.id] });
    },
  });
};

// Удаление локации
export const useDeleteLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deleteLocation(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
};

// Получение статистики локации
export const useLocationStatistics = (id: string | undefined) => {
  return useQuery({
    queryKey: ['locations', id, 'statistics'],
    queryFn: async () => {
      if (!id) throw new Error('Location ID is required');
      const response = await apiClient.get(`/locations/${id}/statistics`);
      return response as LocationStatistics;
    },
    enabled: !!id,
  });
};